import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';

/** One income / expense line read off a lender sheet. */
export interface ScannedAmount {
  /** Mystro type, e.g. 'Base Salary' or 'Child Care'. */
  type: string;
  amount: number;
  frequency: 'Annually' | 'Monthly';
  /** Mystro group the type sits under, e.g. 'Investments' — income only. */
  group?: string;
}

export interface ScannedApplicant {
  firstName: string;
  lastName: string;
  /** Every income group, flattened; all annual. */
  income: ScannedAmount[];
}

/** Rent belongs to a property, so it carries the property and who owns it. */
export interface ScannedRentalIncome {
  type: 'Rental Income';
  relatedRealEstate: string;
  amount: number;
  frequency: 'Annually' | 'Monthly';
  /** Percentages; absent when the sheet doesn't map that applicant's share. */
  ownership: { applicant1?: number; applicant2?: number };
  /** This property's share of the household 'Investment Property Costs'. */
  investmentPropertyExpense: number;
}

/** Only the cells the lender's sheet maps come back. */
export interface LenderSheetScanResult {
  lender: string;
  applicant1?: ScannedApplicant;
  applicant2?: ScannedApplicant;
  rentalIncome: ScannedRentalIncome[];
  /** Household expenses — shared by both applicants. */
  monthlyExpenses: ScannedAmount[];
}

export const calculationLenderSheetApi = createApi({
  reducerPath: 'calculationLenderSheetApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    /** Uploads one .xlsx/.xlsm and reads the cells mapped for that lender. */
    scanLenderSheet: builder.mutation<
      LenderSheetScanResult,
      { lender: string; file: File }
    >({
      query: ({ lender, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `v1/assessments/sheet-scan/${lender}`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (r: ApiResponse<LenderSheetScanResult>) => r.data,
    }),
  }),
});

export const { useScanLenderSheetMutation } = calculationLenderSheetApi;
